// 20 fiches "Combien vaut [secteur]" — pages programmatic SEO long-tail
// Stratégie : capter les recherches très spécifiques type "combien vaut une boulangerie"
// Volume modéré par page mais cumul élevé + intention d'achat/vente très qualifiée.

export const BUSINESSES = [
  {
    slug: 'boulangerie',
    name: 'boulangerie',
    nameLong: 'boulangerie-pâtisserie artisanale',
    article: 'une',
    emoji: '🥖',
    color: '#A87A1C',
    searchVolume: '1900/mois',
    multiplesCa: { min: 0.55, max: 1.10, typical: 0.80 },
    multiplesEbe: { min: 3.5, max: 6, typical: 4.5 },
    example: { ca: 380000, marge: '68%', ebe: 65000, location: 'Lyon', value_low: 210000, value_central: 280000, value_high: 380000 },
    intro: "La boulangerie-pâtisserie est l'un des fonds de commerce les plus échangés en France (plus de 8 000 transactions par an). Sa valeur dépend principalement de 3 critères : l'emplacement, la qualité du bail, et la rentabilité réelle après retraitements.",
    valueFactors: [
      "Emplacement n°1 (passage piéton, école/bureau à proximité, parking) — peut doubler la valeur",
      "Bail commercial long (9 ans renouvelable) avec loyer < 10% du CA",
      "Matériel récent (four, vitrine, chambres froides) — économie 30-80K€ pour le repreneur",
      "Équipe stable avec contrats à durée indéterminée (boulanger/pâtissier qualifié)",
      "CA stable ou croissant sur 3 ans (audit comptable transparent)",
      "Produits propres + clientèle d'habitués (vs dépendance grandes surfaces)",
      "Norme HACCP respectée + aucune mise en demeure DDPP",
    ],
    valuePitfalls: [
      "Dirigeant indispensable (s'il fait pâtisserie + boulange + gestion seul) → décote 25-40%",
      "Loyer > 12% du CA → décote 10-25%",
      "Matériel obsolète (four > 20 ans, chambre froide HS) → coût remplacement déduit",
      "Recettes au noir non documentées → invendable (banque refuse de financer)",
      "Bail expirant dans < 3 ans → forte décote (incertitude renouvellement)",
    ],
    valueOptimizations: [
      "Sécuriser un n°2 boulanger autonome 12-18 mois avant la vente",
      "Renégocier le bail commercial (révision triennale) avant la mise en vente",
      "Investir 15-25K€ pour rafraîchir la vitrine/façade (ROI x3 sur le prix)",
      "Diversifier le CA (traiteur, snacking midi, click & collect) pour réduire la dépendance",
      "Présenter 3 ans de comptes clean (sans charges perso) pour rassurer la banque du repreneur",
    ],
    sectorSpecifics: [
      "Marge brute habituelle : 65-75% (matières premières farine/beurre/sucre)",
      "Saisonnalité : pic Noël (galette des rois, bûches), creux août (vacances)",
      "Réglementation DDPP stricte (chaîne du froid, traçabilité, allergènes)",
      "Convention collective des Boulangeries-pâtisseries (IDCC 843)",
      "Concurrence des chaînes (Marie Blachère, Paul, Brioche Dorée) à intégrer dans l'analyse",
    ],
    buyerProfile: "Boulanger expérimenté souhaitant s'installer (50% des reprises) ou groupe en consolidation régionale (25%) ou investisseur cherchant à diversifier (25%). Financement bancaire courant : 60-80% du prix avec garantie BPI/Siagi.",
    fiscalTip: "Pour la transmission familiale, le Pacte Dutreil s'applique (75% d'abattement sur les droits de mutation). Cession à un repreneur externe : article 150-0 D ter possible si dirigeant en pré-retraite (abattement renforcé).",
  },
  {
    slug: 'restaurant',
    name: 'restaurant',
    nameLong: 'restaurant traditionnel',
    article: 'un',
    emoji: '🍽️',
    color: '#8B2C1C',
    searchVolume: '2400/mois',
    multiplesCa: { min: 0.40, max: 0.90, typical: 0.65 },
    multiplesEbe: { min: 3, max: 5.5, typical: 4 },
    example: { ca: 520000, marge: '70%', ebe: 78000, location: 'Bordeaux', value_low: 230000, value_central: 320000, value_high: 450000 },
    intro: "La restauration traditionnelle représente un marché de plus de 35 milliards d'euros en France. Mais la valorisation d'un restaurant est l'une des plus complexes : entre l'emplacement (premier critère), la qualité du bail, la licence, l'état du matériel cuisine et la rentabilité réelle, les écarts d'une affaire à l'autre peuvent atteindre du simple au triple.",
    valueFactors: [
      "Emplacement (centre-ville touristique, zone d'affaires, axe passant) — multiplie par 1,5 à 2,5",
      "Licence IV (vente d'alcool fort) — valeur additionnelle de 5 000 à 15 000€ selon ville",
      "Terrasse autorisée (très valorisée en zone touristique) — +20-40% sur la valeur",
      "Notation Google/TripAdvisor > 4,2/5 avec > 200 avis — forte prime",
      "Chef qualifié restant après la vente (contrat) — sécurise le repreneur",
      "Concept fort + identité visuelle marquée (vs restaurant générique)",
      "Taux d'occupation soirée > 70% + résa Le Fourchette/TheFork récurrente",
    ],
    valuePitfalls: [
      "Restaurant 100% dépendant du chef-propriétaire → décote 30-50%",
      "Loyer > 15% du CA → décote 20%",
      "Hotte/cuisine ancienne nécessitant > 30K€ de mise aux normes → déduction directe",
      "Taux de no-show élevé / mauvais avis Google récents → décote",
      "Saisonnalité extrême (été uniquement) → multiple réduit (lissage difficile)",
    ],
    valueOptimizations: [
      "Stabiliser une équipe cuisine + salle 6-12 mois avant la vente",
      "Investir 5-10K€ dans la déco et photos pro Google/Instagram",
      "Mettre en place le click & collect / livraison Deliveroo-Uber Eats pour montrer CA résilient",
      "Augmenter le ticket moyen (formule midi, vins, café) — impact direct sur EBE",
      "Présenter 3 ans de Z journalières propres + caisse certifiée NF525",
    ],
    sectorSpecifics: [
      "Marge brute : 65-75% (food cost cible 28-30%, boissons 25-28%)",
      "Saisonnalité : pic mai-septembre (zones touristiques), creux janvier",
      "Réglementation DDPP : HACCP, allergènes, traçabilité, plan de maîtrise sanitaire",
      "Convention collective HCR (Hôtels-Cafés-Restaurants, IDCC 1979)",
      "Licence IV : valeur indépendante du fonds (peut être vendue séparément)",
    ],
    buyerProfile: "Chef cuisinier souhaitant s'établir (45%), couple de restaurateurs (30%), groupe de restauration en consolidation (15%), investisseur (10%). Financement bancaire : 50-70% avec apport minimum 30%.",
    fiscalTip: "Cession après 5 ans d'exploitation : abattement renforcé sur les plus-values (régime article 151 septies si CA < 250K€). Pour la transmission familiale, Pacte Dutreil applicable.",
  },
  {
    slug: 'pharmacie',
    name: 'pharmacie',
    nameLong: "officine de pharmacie",
    article: 'une',
    emoji: '💊',
    color: '#A87A1C',
    searchVolume: '1300/mois',
    multiplesCa: { min: 0.75, max: 1.15, typical: 0.95 },
    multiplesEbe: { min: 5, max: 8, typical: 6.5 },
    example: { ca: 1800000, marge: '28%', ebe: 280000, location: 'Toulouse', value_low: 1500000, value_central: 1800000, value_high: 2200000 },
    intro: "L'officine de pharmacie est l'un des actifs les plus prisés du commerce français. Sa valorisation est encadrée par des règles spécifiques (multiples du CA TTC ou hors taxes selon les cas) et bénéficie d'une stabilité exceptionnelle grâce au monopole pharmaceutique et au remboursement Sécurité Sociale qui sécurise une grande partie du chiffre d'affaires.",
    valueFactors: [
      "CA TTC > 1,5 M€ — multiples plus élevés (effet taille)",
      "Localisation : centre-ville commerçant, près d'un cabinet médical ou maison médicale = prime forte",
      "Mix CA équilibré (médicaments remboursés / OTC / parapharmacie / orthopédie)",
      "Marge brute moyenne > 27% (négociation grossistes optimisée)",
      "Robotisation/automatisation (Rowa, BD Rowa) — réduit les coûts opérationnels",
      "Titulaire restant 6-12 mois en tuilage avec le repreneur",
      "Locaux modernes et accessibles PMR",
    ],
    valuePitfalls: [
      "Officine isolée (zone rurale, déprime démographique) → décote",
      "Mono-prescripteur (1 médecin proche = 60% du CA) → risque concentration",
      "Loyer > 4% du CA TTC → décote (norme officine : 2-3,5%)",
      "Stocks périmés / non maîtrisés → moins-value à l'inventaire de cession",
      "Logiciel métier obsolète (LGPI, Smart Rx) → coût migration à intégrer",
    ],
    valueOptimizations: [
      "Développer la parapharmacie/cosmétique haut de gamme (marges > 40%)",
      "Renforcer le mix orthopédie/maintien à domicile (services à forte marge)",
      "Optimiser les achats via groupements (Pharmavie, Giropharm, Astera)",
      "Mettre en place rendez-vous vaccination/PCR/entretiens pharmaceutiques",
      "Présenter 3 ans de CA TTC + marges officinales/parapharmacie séparées",
    ],
    sectorSpecifics: [
      "Vente strictement réservée aux pharmaciens diplômés (numerus clausus)",
      "Multiples calculés sur le CA TTC (différent du commerce classique HT)",
      "Marge brute encadrée pour médicaments remboursés (marge dégressive lissée MDL)",
      "Convention collective Pharmacie d'officine (IDCC 1996)",
      "Transmission soumise à autorisation préfectorale (ARS) + ordre des pharmaciens",
    ],
    buyerProfile: "Pharmacien adjoint en première installation (60%), titulaire en croissance externe (25%), groupement (Lafayette, Pharmavie, 15%). Financement bancaire spécialisé (Crédit Agricole Officine, BNP Paribas Healthcare) — financement courant à 80-100% du prix.",
    fiscalTip: "Pacte Dutreil applicable en transmission familiale (abattement 75%). Pour le cédant : si > 5 ans d'exploitation et départ en retraite, abattement de 500 000€ sur les plus-values (article 151 septies B).",
  },
  {
    slug: 'coiffeur',
    name: 'salon de coiffure',
    nameLong: 'salon de coiffure & barbier',
    article: 'un',
    emoji: '💇',
    color: '#0A1929',
    searchVolume: '880/mois',
    multiplesCa: { min: 0.40, max: 0.85, typical: 0.60 },
    multiplesEbe: { min: 2.5, max: 4.5, typical: 3.5 },
    example: { ca: 180000, marge: '85%', ebe: 32000, location: 'Marseille', value_low: 70000, value_central: 105000, value_high: 150000 },
    intro: "Le salon de coiffure est un fonds de commerce dont la valeur dépend fortement du fichier client constitué, de la fidélité des collaborateurs et de la qualité de l'emplacement. C'est un secteur où l'écart de valorisation entre un salon \"de quartier\" et un salon de centre-ville peut atteindre du simple au quintuple pour un même CA.",
    valueFactors: [
      "Fichier client digital (logiciel type Planity, Treatwell, Booksy) avec > 500 clients actifs",
      "Équipe stable : 2-4 coiffeurs en CDI restant après la vente",
      "Emplacement (centre-ville, galerie commerciale, axe piéton) — facteur n°1",
      "Concept fort (salon premium, barbershop, bio/végétal) vs salon générique",
      "Mix activité : coupes (60%) + coloration (25%) + ventes produits (15%)",
      "Notation Google > 4,3/5 avec > 100 avis récents",
      "Bail commercial > 5 ans restants",
    ],
    valuePitfalls: [
      "Salon mono-coiffeur (le propriétaire = tout le CA) → décote 30-50% (clientèle non transférable)",
      "Salaires non déclarés (\"au black\") → invendable",
      "Matériel obsolète (fauteuils, bacs, sèche-cheveux > 10 ans)",
      "Forte concurrence à proximité (>3 salons dans un rayon de 200m)",
      "Marque blanche / franchise sans réelle clientèle propre",
    ],
    valueOptimizations: [
      "Digitaliser intégralement la prise de RDV (Planity, Treatwell) — actif transmissible",
      "Lancer une offre barber/homme si pas encore fait (marges premium)",
      "Développer la vente de produits (Kérastase, L'Oréal Pro) — marges 30-40%",
      "Recruter et fidéliser des coiffeurs autonomes (statut salarié ou commission)",
      "Mettre en place un programme de fidélité numérique (carte virtuelle)",
    ],
    sectorSpecifics: [
      "Marge brute très élevée (80-90%, peu de matières premières)",
      "Pas de saisonnalité marquée (légère baisse août, pic décembre)",
      "Convention collective Coiffure (IDCC 2596)",
      "CAP/BP Coiffure obligatoire pour le repreneur (sauf exonération expérience)",
      "Concurrence des chaînes low-cost (Tchip, Saint Algue) et des barbershops indépendants",
    ],
    buyerProfile: "Coiffeur indépendant en première installation (55%), franchise (20%), investisseur déjà propriétaire de salons (15%), reprise familiale (10%). Financement : 60-80% du prix par banque + apport personnel 30-40%.",
    fiscalTip: "Pour les salons à fort EBE (>50K€), structurer la cession via une holding peut optimiser. Régime article 151 septies disponible si CA < 250K€.",
  },
  {
    slug: 'bar-cafe',
    name: 'bar-café',
    nameLong: 'bar-café-brasserie',
    article: 'un',
    emoji: '☕',
    color: '#A87A1C',
    searchVolume: '720/mois',
    multiplesCa: { min: 0.55, max: 1.20, typical: 0.85 },
    multiplesEbe: { min: 3.5, max: 6, typical: 4.5 },
    example: { ca: 280000, marge: '72%', ebe: 52000, location: 'Lille', value_low: 155000, value_central: 240000, value_high: 340000 },
    intro: "Le bar-café-brasserie est un fonds de commerce dont la valeur tient à 60% de l'emplacement et 40% du concept. Les multiples sont plus élevés que pour la restauration classique car la marge brute (boissons) est meilleure et la dépendance au chef cuisinier est moindre.",
    valueFactors: [
      "Licence IV (alcool fort) — indispensable, valeur 8-20K€ selon ville",
      "Emplacement (centre, axe passant, terrasse exposée sud/ouest)",
      "Terrasse autorisée (peut représenter 40% du CA été)",
      "Mix CA équilibré (boissons chaudes 30%, alcools 30%, repas/snacking 40%)",
      "Activité jeu (Loto, PMU, jeux à gratter) — commission stable + flux passant",
      "Équipement bar moderne (tireuses, machine à café pro)",
      "Bail commercial avec activité \"toutes activités\" (liberté pour le repreneur)",
    ],
    valuePitfalls: [
      "Horaires extensifs nécessitant le couple-propriétaire en permanence → décote",
      "Quartier en déclin (fermeture commerces voisins) → décote 20-40%",
      "Loyer > 12% du CA → décote 15-25%",
      "Restrictions municipales sur la terrasse (réduction prévue) → moins-value",
      "Brasseur partenaire avec engagement d'exclusivité > 3 ans → frein repreneur",
    ],
    valueOptimizations: [
      "Diversifier la clientèle (offre brunch, after-work, soirées thématiques)",
      "Digitaliser la commande (QR code menu, paiement Sumup, click & collect)",
      "Augmenter le ticket moyen (formules, vins au verre premium)",
      "Présenter contrats fournisseurs renouvelables + factures Heineken/Pernod transparentes",
      "Investir 5-15K€ dans la déco (vintage, industriel, scandinave) — ROI fort",
    ],
    sectorSpecifics: [
      "Marge brute : boissons chaudes 80%, alcools 65%, repas 65%",
      "Saisonnalité : pic mai-octobre (terrasses), creux janvier-février",
      "Convention collective HCR (Hôtels-Cafés-Restaurants, IDCC 1979)",
      "Licence IV : transmissible mais limitée géographiquement (quota communal)",
      "Concurrence : chaînes (Starbucks, Columbus) + coffee shops indépendants haut de gamme",
    ],
    buyerProfile: "Couple souhaitant s'installer en cafetier (50%), groupe de restauration multi-établissements (25%), investisseur diversifiant (15%), franchise (10%). Financement : 60-70% par la banque + apport 30-40%.",
    fiscalTip: "Si vente avec maintien dans les murs (locataire), bien évaluer la part du fonds vs droit au bail (fiscalités différentes). Article 151 septies possible si CA < 250K€.",
  },
  {
    slug: 'cabinet-medical',
    name: 'cabinet médical',
    nameLong: 'cabinet de médecin généraliste ou spécialiste',
    article: 'un',
    emoji: '🩺',
    color: '#0A1929',
    searchVolume: '590/mois',
    multiplesCa: { min: 0.25, max: 0.60, typical: 0.40 },
    multiplesEbe: { min: 1.5, max: 3.5, typical: 2.5 },
    example: { ca: 180000, marge: '78%', ebe: 75000, location: 'Nantes', value_low: 60000, value_central: 100000, value_high: 160000 },
    intro: "Le cabinet médical est un cas particulier en valorisation : le \"fonds\" en lui-même a peu de valeur intrinsèque (un patient n'appartient pas à un médecin), mais l'emplacement, le matériel et le droit de présentation à un confrère peuvent représenter des sommes significatives — entre 40K€ pour un généraliste rural et 250K€ pour un spécialiste en centre-ville.",
    valueFactors: [
      "Spécialité : généraliste (multiples bas) < dermato/ophtalmo/cardio (multiples élevés)",
      "Emplacement : maison de santé / MSP urbaine = forte demande",
      "Patientèle stable et identifiée (logiciel métier exporté : Hellodoc, Crossway)",
      "Matériel médical récent (echographe, dermatoscope, ECG) — valeur additionnelle",
      "Secrétariat médical en place (interne ou plateforme type Doctolib)",
      "Présentation à un confrère organisée + tuilage 1-3 mois",
      "Conventionnement (secteur 1 ou 2) — affecte le mix tarifaire",
    ],
    valuePitfalls: [
      "Médecin parti depuis > 6 mois → perte rapide de la patientèle",
      "Zone surdotée (refus d'installation possible selon ARS pour certaines spécialités)",
      "Logiciel métier non transférable / données patients incomplètes",
      "Loyer trop élevé pour un seul praticien → marge écrasée",
      "Pas de présentation confrère organisée → cession plus difficile",
    ],
    valueOptimizations: [
      "Inscrire le cabinet sur Doctolib avant la vente (transmission de la patientèle digitalisée)",
      "Recruter un assistant médical (loi 2019) — augmente la capacité et la valeur",
      "Diversifier les actes (téléconsultation, actes techniques rentables)",
      "Organiser une période de tuilage de 2-3 mois (présentation aux patients)",
      "Documenter la patientèle : âge moyen, pathologies, fréquence de consultation",
    ],
    sectorSpecifics: [
      "Pas de vente du \"fonds\" en tant que tel (droit de présentation + matériel + droit au bail)",
      "Conseil de l'Ordre informé obligatoirement (article R4127-86 CSP)",
      "Convention collective spécifique selon mode d'exercice (salariat, libéral, SCM, SCP)",
      "TVA non applicable (exonération des actes médicaux)",
      "Régime fiscal BNC (Bénéfices Non Commerciaux) pour les libéraux",
    ],
    buyerProfile: "Jeune médecin installé en remplacement (50%), médecin en reconversion régionale (25%), groupe médical / maison de santé (15%), repreneur familial (10%). Financement : prêt installation médecin (Crédit Agricole, BNP) à taux préférentiels.",
    fiscalTip: "Cession sous régime article 151 septies BNC (exonération totale si recettes < 90K€, partielle jusqu'à 126K€). Pour les SELARL/SELAS, possible mise en place de holding patrimoniale.",
  },
  {
    slug: 'cabinet-dentaire',
    name: 'cabinet dentaire',
    nameLong: 'cabinet de chirurgien-dentiste',
    article: 'un',
    emoji: '🦷',
    color: '#0EA5E9',
    searchVolume: '480/mois',
    multiplesCa: { min: 0.50, max: 1.00, typical: 0.75 },
    multiplesEbe: { min: 3, max: 5.5, typical: 4 },
    example: { ca: 420000, marge: '52%', ebe: 165000, location: 'Strasbourg', value_low: 320000, value_central: 450000, value_high: 600000 },
    intro: "Le cabinet dentaire est l'un des cabinets médicaux les mieux valorisés, en raison de son équipement coûteux (panoramique, scanner 3D, fauteuils) et de sa clientèle souvent fidèle. Les multiples sont 2 à 3 fois supérieurs à ceux d'un cabinet de médecin généraliste.",
    valueFactors: [
      "Matériel récent : fauteuil moderne (40-80K€), pano 3D (60-120K€), CFAO/Cerec",
      "Équipe en place : 1-2 assistantes dentaires + secrétaire qui restent",
      "Patientèle active (> 1 500 patients vus dans les 24 derniers mois)",
      "Mix d'activité équilibré (omnipratique 50%, implanto 25%, ortho/esthétique 25%)",
      "Logiciel métier transmissible (Logos, Julie, Veasy)",
      "Cabinet pluri-praticiens (associé ou collaborateur déjà en place)",
      "Présentation à un confrère + tuilage formalisé",
    ],
    valuePitfalls: [
      "Cabinet mono-praticien sans collaborateur → cession plus complexe",
      "Matériel obsolète (fauteuil > 15 ans, pas de pano numérique) → mise à jour 60-150K€",
      "Patientèle vieillissante (âge moyen > 65 ans) → renouvellement plus difficile",
      "Convention CPAM (panier 100% santé) mal gérée → marge dégradée",
      "Pas de stratégie implanto/esthétique → potentiel rentabilité limité",
    ],
    valueOptimizations: [
      "Investir dans le CFAO (CEREC, Trios) avant la vente — actif valorisé directement",
      "Développer l'implantologie (formation + matériel) — actes très rentables",
      "Recruter un collaborateur 12-18 mois avant la vente (sécurise la transmission)",
      "Digitaliser la patientèle (RDV en ligne, devis numériques, dossier médical informatisé)",
      "Présenter 3 ans de comptes propres avec ratio production/équipement",
    ],
    sectorSpecifics: [
      "Convention collective Cabinets dentaires (IDCC 1619)",
      "Conseil de l'Ordre des Chirurgiens-Dentistes informé obligatoirement",
      "TVA non applicable sur les actes médicaux remboursés",
      "Régime fiscal BNC (libéral) ou IS (SEL)",
      "Matériel = actif important du bilan (à réévaluer en valeur de marché)",
    ],
    buyerProfile: "Jeune dentiste après thèse + remplacements (45%), dentiste expérimenté en croissance externe (30%), groupe dentaire (Dentalia, Dentego, 15%), reprise par associé en place (10%).",
    fiscalTip: "Article 151 septies BNC applicable selon CA. Pour les SELARL : intérêt fort à structurer via holding (cession des parts vs cession du fonds). Pacte Dutreil pour transmission familiale.",
  },
  {
    slug: 'agence-immobiliere',
    name: 'agence immobilière',
    nameLong: 'agence immobilière indépendante',
    article: 'une',
    emoji: '🏠',
    color: '#0A1929',
    searchVolume: '720/mois',
    multiplesCa: { min: 0.30, max: 0.80, typical: 0.55 },
    multiplesEbe: { min: 3, max: 5, typical: 4 },
    example: { ca: 450000, marge: '92%', ebe: 110000, location: 'Bordeaux', value_low: 250000, value_central: 380000, value_high: 520000 },
    intro: "L'agence immobilière est un secteur très volatil dans la valorisation, car son CA dépend du cycle immobilier (transactions, vente, location, gestion). Les agences avec un fort volet \"gestion locative\" sont mieux valorisées que les agences de transaction pure car le revenu est récurrent et prévisible.",
    valueFactors: [
      "Mix d'activité : transaction (volatile) vs gestion locative (récurrent et prévisible)",
      "Portefeuille de gestion : nombre de lots gérés × 600-1 200€/lot/an",
      "Carte professionnelle (Loi Hoguet) — obligatoire et transmissible",
      "Équipe stable : négociateurs avec ancienneté et portefeuille personnel",
      "Notoriété locale (avis Google, présence sur les portails)",
      "Réseau ou indépendance (franchise type Century 21, ORPI, Stéphane Plaza)",
      "Outils CRM/logiciel métier moderne (Hektor, Apimo, Netty)",
    ],
    valuePitfalls: [
      "Agence 100% transaction (pas de gestion locative) → CA très volatil → décote",
      "Patron-négociateur indispensable (50%+ des transactions) → décote forte",
      "Mandats exclusifs faibles (< 30%) → faible défensibilité",
      "Logiciel métier non transmissible / fichier prospects mal entretenu",
      "Convention avec syndic incompatible avec un repreneur",
    ],
    valueOptimizations: [
      "Développer la gestion locative (acquisitions de portefeuilles, démarchage propriétaires)",
      "Structurer un fichier acquéreurs/vendeurs propre et exploitable",
      "Améliorer les avis Google (passer à > 4,5/5 avec > 100 avis)",
      "Recruter un n°2 commercial autonome 12-18 mois avant la vente",
      "Présenter ratios précis : commission moyenne, taux mandats exclusifs, durée moyenne mandat",
    ],
    sectorSpecifics: [
      "Carte professionnelle T (transaction) et/ou G (gestion) obligatoire",
      "Garantie financière obligatoire (Galian, CGAIM, GAC)",
      "Convention collective Immobilier (IDCC 1527)",
      "Soumise à la loi Hoguet (1970) et à la loi ALUR (2014)",
      "TVA sur commissions, exonération sur les loyers gérés",
    ],
    buyerProfile: "Négociateur expérimenté en première installation (40%), réseau/franchise en expansion (30%), agence concurrente en consolidation (20%), investisseur (10%). Financement : 50-70% par la banque + garantie BPI.",
    fiscalTip: "Pour les agences à forte valorisation, structurer via holding. Article 151 septies B disponible si départ en retraite > 5 ans d'exploitation.",
  },
  {
    slug: 'expert-comptable',
    name: "cabinet d'expertise comptable",
    nameLong: "cabinet d'expertise comptable",
    article: 'un',
    emoji: '🧾',
    color: '#0A1929',
    searchVolume: '320/mois',
    multiplesCa: { min: 0.90, max: 1.40, typical: 1.15 },
    multiplesEbe: { min: 4, max: 7, typical: 5.5 },
    example: { ca: 850000, marge: '88%', ebe: 220000, location: 'Lille', value_low: 850000, value_central: 1000000, value_high: 1250000 },
    intro: "Le cabinet d'expertise comptable est l'un des actifs professionnels libéraux les plus prisés en France, en raison de la récurrence quasi-totale de son CA (90%+ des clients restent d'année en année). Les multiples appliqués sont les plus élevés du secteur des services aux entreprises (jusqu'à 1,4x CA).",
    valueFactors: [
      "Récurrence du CA : 90%+ des clients renouvelés chaque année",
      "Mix clientèle : TPE/PME (panier moyen 2 500-5 000€/an) ou GE (paniers > 15 000€)",
      "Effectif stable : collaborateurs DCG/DSCG avec ancienneté",
      "Outils digitaux modernes (Cegid Loop, Pennylane, Sage Genapi)",
      "Spécialisation sectorielle (BTP, médical, restauration) — valorisée",
      "Présentation des dossiers à un confrère + tuilage 6-12 mois",
      "Cabinet pluri-experts (au moins 2 EC inscrits)",
    ],
    valuePitfalls: [
      "Cabinet mono-EC où le titulaire fait tous les RDV clients → décote 25-40%",
      "Clientèle vieillissante (chefs d'entreprise > 60 ans) → renouvellement incertain",
      "Outils anciens (Sage Coala, Ciel non migrés) → coût modernisation 30-80K€",
      "Forte dépendance à 2-3 gros clients (> 40% du CA)",
      "Tarifs sous-évalués (< moyenne marché) → repreneur doit augmenter (risque churn)",
    ],
    valueOptimizations: [
      "Migrer vers Pennylane/Cegid Loop avant la vente (modernise l'image)",
      "Standardiser et documenter les process (manuel qualité)",
      "Identifier et augmenter les tarifs des clients sous-facturés",
      "Recruter un expert-comptable associé (sécurise la transmission)",
      "Décomposer le CA par mission (compta, social, juridique, conseil) pour clarifier la valeur",
    ],
    sectorSpecifics: [
      "Inscription à l'Ordre des Experts-Comptables obligatoire pour le repreneur",
      "Conseil régional informé de la cession",
      "Convention collective des cabinets d'expertise comptable (IDCC 787)",
      "TVA sur les honoraires (sauf clients en franchise)",
      "Régime BNC (libéral) ou IS (SEL/SCP)",
    ],
    buyerProfile: "Expert-comptable en première installation (35%), cabinet en croissance externe (40%), groupe national (Fiducial, In Extenso, Compagnie Fiduciaire, 20%), reprise interne par associé (5%).",
    fiscalTip: "Cession via SEL : régime des plus-values sur titres avec abattement renforcé si départ en retraite. Pacte Dutreil applicable pour transmission familiale. Holding patrimoniale recommandée pour cabinets > 500K€ valo.",
  },
  {
    slug: 'garage-automobile',
    name: 'garage automobile',
    nameLong: 'garage automobile mécanique-carrosserie',
    article: 'un',
    emoji: '🔧',
    color: '#475569',
    searchVolume: '590/mois',
    multiplesCa: { min: 0.30, max: 0.65, typical: 0.45 },
    multiplesEbe: { min: 3, max: 5, typical: 4 },
    example: { ca: 620000, marge: '55%', ebe: 95000, location: 'Reims', value_low: 240000, value_central: 330000, value_high: 460000 },
    intro: "Le garage automobile (mécanique générale, entretien, carrosserie) est un fonds de commerce dont la valeur tient principalement à la clientèle d'habitués, à la qualité du matériel et à la localisation. La concurrence des chaînes (Norauto, Feu Vert, Speedy) et l'évolution vers l'électrique transforment progressivement le marché.",
    valueFactors: [
      "Localisation avec zone d'activité ou axe passant",
      "Spécialisation valorisée (carrosserie peinture, multi-marques, agrément expert auto)",
      "Agrément constructeur (BMW Service, Renault Agréé) — clientèle assurée",
      "Matériel récent : pont élévateur, géométrie 3D, banc de diagnostic, cabine peinture",
      "Équipe technique stable (CAP/BTS mécanique, qualifications gaz climatisation)",
      "Carnet de rendez-vous stable (visibilité 2-4 semaines)",
      "Surface technique > 200 m² + parking client + atelier conforme normes ICPE",
    ],
    valuePitfalls: [
      "Patron-mécano indispensable (>50% des heures facturées par lui) → décote 30%",
      "Matériel obsolète (pont > 20 ans, pas de diagnostic récent) → coût remplacement",
      "Mises aux normes ICPE en attente (collecte huiles, ventilation peinture) → coût direct",
      "Clientèle vieillissante (véhicules essence/diesel ancien) sans plan transition électrique",
      "Loyer > 12% du CA → décote (atelier souvent en zone industrielle louée)",
    ],
    valueOptimizations: [
      "Investir dans le matériel diagnostic moderne (multi-marques type Texa, Snap-on)",
      "Former 1-2 mécaniciens à l'électrique/hybride (qualifs IRVE, B0L, B1XL, B2XL)",
      "Décrocher un agrément constructeur ou un partenariat assurances (réparations)",
      "Diversifier (vente VO d'occasion, location courte durée) — marges additionnelles",
      "Présenter ratios précis : taux horaire facturé, heures vendues/mois, % carrosserie/mécanique",
    ],
    sectorSpecifics: [
      "Convention collective Services de l'Automobile (IDCC 1090)",
      "Activité ICPE possible si > 500 m² (déclaration ou autorisation préfectorale)",
      "Garantie biennale obligatoire sur réparations",
      "Adhésion ANIA, MMA garage, ou assurance pro RC obligatoire",
      "Transition électrique : enjeu majeur des 5 prochaines années",
    ],
    buyerProfile: "Mécanicien expérimenté en première installation (45%), groupe automobile (concession en consolidation, 25%), repreneur familial (15%), franchise (Speedy, Midas, 15%). Financement : 60-70% par la banque + apport 30-40%.",
    fiscalTip: "Article 151 septies si CA < 250K€. Cession des murs séparément du fonds peut optimiser fiscalement. Régime BIC.",
  },
  {
    slug: 'hotel',
    name: 'hôtel',
    nameLong: 'hôtel indépendant 2 à 4 étoiles',
    article: 'un',
    emoji: '🏨',
    color: '#0F172A',
    searchVolume: '590/mois',
    multiplesCa: { min: 1.20, max: 3.20, typical: 2.20 },
    multiplesEbe: { min: 6, max: 10, typical: 8 },
    example: { ca: 850000, marge: '78%', ebe: 220000, location: 'Nice', value_low: 1000000, value_central: 1750000, value_high: 2700000 },
    intro: "L'hôtellerie est l'un des secteurs où la valorisation est la plus complexe : elle combine le fonds de commerce (exploitation), les murs (immobilier), le mobilier, et parfois la marque. Les multiples sont les plus élevés du commerce traditionnel mais varient énormément selon le classement (1 à 5 étoiles), l'emplacement et le taux d'occupation.",
    valueFactors: [
      "Classement étoile (1★ à 5★) — multiple croissant avec les étoiles",
      "Taux d'occupation moyen annuel > 65% (référence sectorielle)",
      "RevPAR (Revenue per Available Room) — métrique clé pour les acquéreurs",
      "Emplacement : centre-ville touristique, gare, aéroport, zone d'affaires",
      "État du bâti : rénovations récentes (chambres, parties communes, accessibilité PMR)",
      "Notation Booking/TripAdvisor > 8,5/10 avec > 500 avis",
      "Activité complémentaire : restaurant, bar, spa, séminaires (lisse la saisonnalité)",
    ],
    valuePitfalls: [
      "Murs séparés du fonds (loyer hôtelier élevé) → décote sur le fonds",
      "Saisonnalité extrême (été uniquement) → multiple réduit",
      "Travaux à prévoir (mise aux normes incendie, PMR, climatisation) → déduction directe",
      "Forte dépendance à Booking.com (> 50% des réservations) — marge réduite (commission 15-25%)",
      "Note Booking < 7/10 ou baisse récente → décote",
    ],
    valueOptimizations: [
      "Augmenter le taux d'occupation via yield management (Lighthouse, Pricepoint)",
      "Diversifier les canaux de distribution (site direct, Airbnb, agences corporate)",
      "Investir dans la rénovation des chambres (ROI à 3-5 ans)",
      "Améliorer la note Booking/Google (réponses systématiques, amélioration accueil)",
      "Présenter 3 ans d'indicateurs précis : RevPAR, ADR, taux d'occupation par mois",
    ],
    sectorSpecifics: [
      "Classement officiel par Atout France (renouvellement tous les 5 ans)",
      "Convention collective HCR (IDCC 1979)",
      "Taxe de séjour applicable + déclarations préfecture",
      "TVA hôtelière (10% sur l'hébergement)",
      "Normes incendie ERP (Établissement Recevant du Public) à respecter",
    ],
    buyerProfile: "Investisseur immobilier (40%), groupe hôtelier (B&B, Logis, Best Western en franchise, 30%), exploitant indépendant (20%), fonds d'investissement (10%). Financement : crédit-bail immobilier, prêt hôtelier (BPI, Crédit Foncier).",
    fiscalTip: "Cession des murs + fonds en séparé : optimisation possible via SCI familiale + holding. Article 151 septies B si départ en retraite. Pacte Dutreil très utilisé en hôtellerie familiale.",
  },
  {
    slug: 'cabinet-veterinaire',
    name: 'cabinet vétérinaire',
    nameLong: 'cabinet vétérinaire (chiens-chats / mixte)',
    article: 'un',
    emoji: '🐾',
    color: '#A87A1C',
    searchVolume: '320/mois',
    multiplesCa: { min: 0.40, max: 0.90, typical: 0.65 },
    multiplesEbe: { min: 3, max: 5.5, typical: 4 },
    example: { ca: 380000, marge: '72%', ebe: 90000, location: 'Rennes', value_low: 200000, value_central: 290000, value_high: 380000 },
    intro: "Le cabinet vétérinaire est un secteur en très forte croissance depuis 10 ans (+5% par an), porté par l'augmentation du nombre d'animaux de compagnie et la sophistication des soins. Les multiples ont nettement augmenté et l'arrivée de groupes (IVC Evidensia, AniCura, VetPartners) tire les valorisations vers le haut.",
    valueFactors: [
      "Spécialisation : chiens-chats (multiples standards), équin (élevés), bovin/rural (modérés)",
      "Équipement médical moderne (radio, écho, analyseur sang, anesthésie gazeuse)",
      "Bloc opératoire fonctionnel + équipement chirurgie",
      "Équipe : 1-2 ASV (auxiliaire spécialisé vétérinaire) qualifiées qui restent",
      "Patientèle active (animaux vus dans les 18 derniers mois)",
      "Notation Google/Doctolib > 4,5/5",
      "Logiciel métier (Vetolib, Bourgelat, Vetogest) — fichier transmissible",
    ],
    valuePitfalls: [
      "Cabinet mono-vétérinaire (titulaire qui fait toutes les consultations) → décote 30-40%",
      "Matériel obsolète (radio argentique, pas d'écho, anesthésie liquide) → coût remplacement 50-150K€",
      "Local exigu (< 80 m²) sans possibilité d'extension",
      "Pas de garde / urgences (clientèle perdue)",
      "Tarifs sous-évalués vs benchmark → repreneur doit augmenter (risque)",
    ],
    valueOptimizations: [
      "Recruter un confrère ou ASV 12-18 mois avant la vente",
      "Investir dans l'écho/radio numérique + analyseur sang biochimie",
      "Développer les services additionnels (boutique alimentation, accessoires, ostéopathie)",
      "Mettre en place le suivi vaccination et soins préventifs (récurrence du CA)",
      "Présenter 3 ans de comptes propres + ratios par type d'acte (consult, chir, hospi)",
    ],
    sectorSpecifics: [
      "Inscription à l'Ordre des Vétérinaires obligatoire",
      "Convention collective Cabinets vétérinaires (IDCC 1875)",
      "TVA sur les actes vétérinaires (sauf vente médicaments humains)",
      "Réglementation pharmaceutique vétérinaire (achat groupé Centravet/Alcyon)",
      "Demande croissante (taux possession animal en hausse, sophistication soins)",
    ],
    buyerProfile: "Vétérinaire en première installation (35%), confrère en croissance externe (25%), groupe vétérinaire (IVC Evidensia, AniCura, VetPartners, 30%), reprise par associé (10%).",
    fiscalTip: "Les groupes vétérinaires achètent souvent via SPFPL (Société de Participation Financière des Professions Libérales). Pour le cédant : possibilité de réinvestir en SPFPL et différer la fiscalité.",
  },
  {
    slug: 'tabac-presse',
    name: 'tabac-presse',
    nameLong: 'tabac-presse-jeux-PMU',
    article: 'un',
    emoji: '🚬',
    color: '#A16207',
    searchVolume: '480/mois',
    multiplesCa: { min: 1.0, max: 2.5, typical: 1.6 },
    multiplesEbe: { min: 5, max: 8, typical: 6.5 },
    example: { ca: 950000, marge: '15%', ebe: 105000, location: 'Dijon', value_low: 850000, value_central: 1500000, value_high: 2200000 },
    intro: "Le bureau de tabac-presse-jeux est un fonds de commerce atypique : sa valeur est calculée principalement sur le CA tabac (réglementé par les Douanes) et sur la commission tabac, complétée par les autres activités (presse, FDJ, PMU, papeterie). Les multiples sont parmi les plus élevés du commerce de détail.",
    valueFactors: [
      "CA tabac > 800K€ (seuil de rentabilité standard)",
      "Commission tabac (10,8% en moyenne) — base principale du revenu",
      "Activité PMU/Loto/Amigo — commission FDJ représente 10-25% du CA",
      "Emplacement avec flux passant (axe commerçant, station, gare)",
      "Diversification (papeterie, e-cigarettes, dépôt-presse, café à emporter)",
      "Bail commercial long et licence Douanes pérenne",
      "Acceptation de modes de paiement modernes (CB, sans contact, paiement mobile)",
    ],
    valuePitfalls: [
      "Baisse continue du CA tabac (-2 à -5% par an depuis 2018) — risque structurel",
      "Buraliste indispensable (ouverture 7j/7 fatigante)",
      "Locaux vieillissants nécessitant rafraîchissement (coffre tabac sécurité, vitrine)",
      "Concentration sur un seul revenu (100% tabac) sans diversification",
      "Quartier en déclin commercial → CA papeterie et flux passant en baisse",
    ],
    valueOptimizations: [
      "Diversifier vers le snacking / boissons / café (marges 50%+)",
      "Devenir relais colis (Mondial Relay, Relais Pickup) — flux additionnel gratuit",
      "Développer les jeux à gratter / Loto en bornes self-service",
      "Investir dans la vitrine et la signalétique (visibilité de loin)",
      "Présenter 3 ans de Z journaux avec décomposition par activité (tabac, jeux, presse)",
    ],
    sectorSpecifics: [
      "Vente tabac réglementée par les Douanes (numerus clausus, agrément)",
      "Transmission soumise à l'agrément du gérant (instruction DGDDI)",
      "Convention collective Buralistes (IDCC 1564)",
      "TVA sur tabac (encaissement pour le compte de l'État)",
      "Forte évolution réglementaire (vape, paquet neutre, taxes)",
    ],
    buyerProfile: "Buraliste indépendant en première installation (50%), repreneur sur 2-3 affaires en concentration (25%), franchise tabac-presse (15%), reprise familiale (10%). Financement : 60-80% par la banque + apport 20-40%.",
    fiscalTip: "Cession sous régime BIC. Si murs détenus en parallèle (SCI), bien dissocier les ventes. Article 151 septies disponible selon CA. Pacte Dutreil pour transmission familiale.",
  },
  {
    slug: 'saas-startup',
    name: 'startup SaaS',
    nameLong: 'startup SaaS B2B (Software as a Service)',
    article: 'une',
    emoji: '💻',
    color: '#0A1929',
    searchVolume: '480/mois',
    multiplesCa: { min: 4, max: 12, typical: 7, basis: 'ARR (Annual Recurring Revenue)' },
    multiplesEbe: { min: 0, max: 20, typical: 0, comment: 'rarement appliqué — multiples sur ARR plutôt' },
    example: { ca: 600000, marge: '88%', ebe: 50000, location: 'Paris', value_low: 2400000, value_central: 4200000, value_high: 7200000 },
    intro: "La startup SaaS est valorisée selon des méthodes spécifiques qui n'ont rien à voir avec le commerce traditionnel. La métrique clé est l'ARR (Annual Recurring Revenue, revenu récurrent annualisé), pas l'EBITDA. Les multiples vont de 4x ARR (SaaS modeste, faible croissance) à 12x ARR (hyper-growth, marché premium).",
    valueFactors: [
      "ARR (Annual Recurring Revenue) > 500K€ + croissance YoY > 50%",
      "Net Revenue Retention (NRR) > 110% (les clients dépensent plus chaque année)",
      "Churn brut < 8% par an (clients qui quittent)",
      "Marge brute > 75% (typique du SaaS bien construit)",
      "CAC payback < 18 mois (temps pour rentabiliser un nouveau client)",
      "Diversification clients (aucun client > 10% du CA)",
      "Stack technique moderne (cloud, scalable, équipe dev en place)",
    ],
    valuePitfalls: [
      "Croissance < 20%/an → multiple s'effondre (5x → 2-3x ARR)",
      "Churn > 15% → marché ne croit pas en la rétention",
      "Dépendance à 1-2 gros clients (> 30% du CA) → risque concentration",
      "Pas de PMF clair (mix de petits clients différents, pas de segment dominant)",
      "Stack technique legacy / dette technique importante → coût refonte",
    ],
    valueOptimizations: [
      "Démontrer l'expansion (upsell, cross-sell) — l'arme magique des SaaS premium",
      "Réduire le churn (renforcer le customer success, contrats annuels)",
      "Pricing par tier avec montée en gamme claire (Bronze → Silver → Gold)",
      "Internationaliser (premiers clients USA/UK/DACH) — multiplie les acquéreurs potentiels",
      "Présenter cohort analysis, LTV, CAC, NRR avec dashboards propres (Metabase, Looker)",
    ],
    sectorSpecifics: [
      "Multiples basés sur ARR, pas EBITDA (souvent négatif ou très faible)",
      "Forte demande des fonds (VC, Private Equity) et des stratégiques (logiciels grands groupes)",
      "Valorisations volatiles (en 2021 : 15-20x ARR, en 2024 : 4-8x, en 2026 : 6-12x)",
      "Convention collective Syntec (IDCC 1486)",
      "Stack tech à valoriser (IP, code propriétaire, certifications type SOC2)",
    ],
    buyerProfile: "Fonds de Private Equity (PE) tech (40%), grand groupe en consolidation (25%), VC pour build-up (20%), management buy-out (15%). Process structuré via banquier d'affaires souvent recommandé (Cambon, Aelios, Edelweiss).",
    fiscalTip: "Cession de parts (vs cession d'actifs) presque toujours préférée fiscalement. Holding patrimoniale + report d'imposition (article 150-0 B ter) souvent mis en place. BSPCE pour les salariés clés.",
  },
  {
    slug: 'cabinet-conseil',
    name: 'cabinet de conseil',
    nameLong: 'cabinet de conseil (stratégie / management / IT)',
    article: 'un',
    emoji: '💼',
    color: '#0EA5E9',
    searchVolume: '320/mois',
    multiplesCa: { min: 0.60, max: 1.40, typical: 0.95 },
    multiplesEbe: { min: 4, max: 7, typical: 5.5 },
    example: { ca: 750000, marge: '85%', ebe: 130000, location: 'Paris', value_low: 550000, value_central: 750000, value_high: 1000000 },
    intro: "Le cabinet de conseil (stratégie, management, IT, RH, transformation) est un actif intellectuel valorisé principalement sur la récurrence du CA, la qualité du portefeuille clients et la marque employeur. Les multiples varient fortement selon la spécialisation et la taille du cabinet.",
    valueFactors: [
      "Récurrence du CA (> 50% chez les mêmes clients d'une année sur l'autre)",
      "Portefeuille clients premium (CAC 40, ETI, secteur public) — multiples élevés",
      "Mix consultants : taux de senior (> 5 ans XP) > 40%",
      "Marque/notoriété (Présence dans presse, awards, conférences)",
      "Méthodologies propriétaires + IP documentée",
      "Équipe stable avec ancienneté moyenne > 3 ans",
      "Pipeline commercial visible 6 mois minimum",
    ],
    valuePitfalls: [
      "Cabinet 100% dépendant des associés fondateurs → décote 30-50%",
      "Forte rotation des consultants (> 20%/an) → coût recrutement intégré",
      "Clientèle concentrée (1-2 clients > 30% du CA) → risque attrition",
      "TJM (Tarif Journalier Moyen) sous-évalué vs marché → repreneur doit augmenter",
      "Pas de production propre (slides, frameworks, IP) → cabinet \"juste de la main d'œuvre\"",
    ],
    valueOptimizations: [
      "Promouvoir des consultants seniors en \"partners\" 12-24 mois avant la vente",
      "Documenter et productiser les méthodologies (frameworks, templates, IP)",
      "Réduire la dépendance aux 2-3 gros clients (acquisition nouveaux logos)",
      "Augmenter le TJM (positionnement premium, montée en gamme)",
      "Présenter dashboards : taux d'utilisation, TJM par senior/junior, marge par mission",
    ],
    sectorSpecifics: [
      "Convention collective Syntec (IDCC 1486)",
      "Activité libérale ou SAS — fiscalité différente",
      "TVA 20% sur les honoraires (sauf prestations export)",
      "Forte demande des grands cabinets (Capgemini, Accenture, Deloitte, EY) pour consolidation",
      "Marché en croissance : transformation digitale, IA, ESG, cybersécurité",
    ],
    buyerProfile: "Grand cabinet en croissance externe (40%), Private Equity (Naxicap, Andera, 25%), management buy-out par consultants seniors (20%), groupe industriel internalisant son conseil (15%).",
    fiscalTip: "Cession via holding souvent optimisée (article 150-0 B ter pour le report). Pacte Dutreil pour transmission familiale. BSPCE/Actions gratuites pour fidéliser les consultants clés avant la vente.",
  },
  {
    slug: 'auto-ecole',
    name: 'auto-école',
    nameLong: "auto-école / établissement d'enseignement de la conduite",
    article: 'une',
    emoji: '🚗',
    color: '#A87A1C',
    searchVolume: '320/mois',
    multiplesCa: { min: 0.30, max: 0.75, typical: 0.50 },
    multiplesEbe: { min: 3, max: 5, typical: 4 },
    example: { ca: 280000, marge: '60%', ebe: 55000, location: 'Montpellier', value_low: 100000, value_central: 165000, value_high: 240000 },
    intro: "L'auto-école est un fonds de commerce de proximité dont la valeur dépend du nombre d'élèves inscrits, du taux de réussite à l'examen, de la flotte de véhicules et de la qualité de l'emplacement. La concurrence des plateformes en ligne (Ornikar, Lepermislibre) a transformé le secteur depuis 2015.",
    valueFactors: [
      "Nombre d'élèves actifs (> 150 = bon volume)",
      "Taux de réussite au permis B (> 65% = au-dessus moyenne nationale)",
      "Flotte véhicules récente (Renault Clio, Citroën C3 dernière génération)",
      "Agrément préfectoral à jour (BEPECASER ou TP-ECSR pour le gérant)",
      "Mix examens (B, A2, AAC conduite accompagnée, B96)",
      "Présence numérique (site web, prise de RDV en ligne, partenariats Carte Pass'Permis)",
      "Bail commercial avec local accueil + parking",
    ],
    valuePitfalls: [
      "Forte concurrence des plateformes en ligne (Ornikar 30% moins cher)",
      "Moniteurs en CDD/intérim → décote (besoin recrutement après vente)",
      "Véhicules anciens (> 5 ans) → coût renouvellement 60-120K€",
      "Taux de réussite < 55% → mauvaise réputation, fuite des inscrits",
      "Quartier mal desservi (pas de zone d'examen à proximité)",
    ],
    valueOptimizations: [
      "Digitaliser l'inscription et la pédagogie (En Voiture Simone, codes en ligne)",
      "Renouveler la flotte véhicules avant la vente (impact direct sur la valeur)",
      "Diversifier les permis (moto, BSR, conduite accompagnée, post-permis)",
      "Améliorer le taux de réussite (sélection des candidats, contrôle qualité moniteurs)",
      "Présenter 3 ans de bilans + indicateurs clés (élèves inscrits, taux réussite, CA par moniteur)",
    ],
    sectorSpecifics: [
      "Agrément préfectoral obligatoire (renouvelable tous les 5 ans)",
      "Convention collective Services de l'Automobile (IDCC 1090) section auto-écoles",
      "TVA 20% sur les forfaits",
      "Réglementation stricte (durée minimum formation, livret apprentissage)",
      "Concurrence des plateformes (Ornikar, Lepermislibre) — challenge structurel",
    ],
    buyerProfile: "Moniteur expérimenté en première installation (50%), groupe d'auto-écoles en consolidation (25%), franchise (CER, ECF, 15%), reprise familiale (10%). Financement : 60-70% par la banque + apport 30-40%.",
    fiscalTip: "Article 151 septies applicable selon CA. Cession des véhicules (souvent en LOA/LLD) à dissocier du fonds. Régime BIC.",
  },
  {
    slug: 'institut-beaute',
    name: 'institut de beauté',
    nameLong: 'institut de beauté / SPA / esthétique',
    article: 'un',
    emoji: '💅',
    color: '#0A1929',
    searchVolume: '320/mois',
    multiplesCa: { min: 0.45, max: 0.95, typical: 0.65 },
    multiplesEbe: { min: 3, max: 5, typical: 4 },
    example: { ca: 220000, marge: '78%', ebe: 42000, location: 'Strasbourg', value_low: 100000, value_central: 145000, value_high: 210000 },
    intro: "L'institut de beauté est un fonds de commerce dont la valorisation se rapproche de celle du salon de coiffure : forte dépendance à l'emplacement, à la clientèle fidélisée et à la qualité des esthéticiennes. Les instituts qui ont su développer une offre signature (épilation laser, soins corps premium, photothérapie) sont nettement mieux valorisés.",
    valueFactors: [
      "Fichier clients actif (> 400 clientes avec historique)",
      "Équipement haut de gamme (laser épilation, photothérapie, microdermabrasion)",
      "Cabines équipées (table de soin, douche, vapeur, infrarouge)",
      "Esthéticiennes qualifiées (CAP/BP) restant après la vente",
      "Mix CA : soins (60%), épilation (25%), vente produits (15%)",
      "Marque positionnée (institut premium vs low-cost) avec photos professionnelles",
      "Réservation en ligne (Planity, Treatwell, site direct)",
    ],
    valuePitfalls: [
      "Institut mono-esthéticienne (la propriétaire) → décote 30-50%",
      "Équipement laser non amorti / location-bail à reprendre",
      "Locaux exigus (< 50 m²) limitant le nombre de cabines",
      "Forte concurrence à proximité (3+ instituts dans 300m)",
      "Absence de programme fidélité ou de digitalisation",
    ],
    valueOptimizations: [
      "Investir dans une machine signature (laser, cryolipolyse) — actif valorisé",
      "Lancer une cabine ou marque blanche (Sothys, Decléor, Yon-Ka) — image renforcée",
      "Recruter et former 1-2 esthéticiennes autonomes 12 mois avant",
      "Digitaliser la prise de RDV + relances SMS automatiques",
      "Augmenter la vente de produits (objectif 20% du CA, marges 35-50%)",
    ],
    sectorSpecifics: [
      "Convention collective Esthétique cosmétique (IDCC 3032)",
      "CAP esthétique cosmétique obligatoire pour le repreneur",
      "TVA 20% sur les prestations et produits",
      "Réglementation stricte sur les appareils (laser, photothérapie) — DM classe IIa+",
      "Marché en croissance (+3 à 5%/an), demande masculine en hausse",
    ],
    buyerProfile: "Esthéticienne expérimentée en première installation (50%), franchise (Yves Rocher, Nocibé Spa, 20%), groupe spa/wellness (15%), reprise familiale (15%).",
    fiscalTip: "Article 151 septies si CA < 250K€. Pour les SPA importants (CA > 500K€), structuration en holding intéressante. Régime BIC.",
  },
  {
    slug: 'bureau-etudes',
    name: "bureau d'études",
    nameLong: "bureau d'études techniques (BET) — ingénierie",
    article: 'un',
    emoji: '📐',
    color: '#0A1929',
    searchVolume: '210/mois',
    multiplesCa: { min: 0.60, max: 1.20, typical: 0.90 },
    multiplesEbe: { min: 4, max: 6.5, typical: 5 },
    example: { ca: 480000, marge: '82%', ebe: 95000, location: 'Grenoble', value_low: 380000, value_central: 480000, value_high: 620000 },
    intro: "Le bureau d'études techniques (BET) est un cabinet d'ingénierie (structure, fluides, environnement, mécanique, électrotechnique, etc.) valorisé sur la récurrence des missions, le portefeuille clients et la qualification des ingénieurs. La spécialisation (CVC, BIM, AMO, MOE) et les qualifications OPQIBI font fortement varier les multiples.",
    valueFactors: [
      "Qualifications OPQIBI ou OPQTECC (cabinet inscrit dans les standards)",
      "Spécialisation valorisée (structure béton, BIM, transition énergétique, AMO)",
      "Carnet de commandes visible > 6-12 mois",
      "Récurrence des donneurs d'ordre (maîtres d'œuvre, promoteurs, collectivités)",
      "Équipe ingénieurs/techniciens stable avec ancienneté > 3 ans",
      "Logiciels métiers (Autocad, Revit, Robobat, Allplan) — licences à jour",
      "Mix public/privé équilibré (résilience marché)",
    ],
    valuePitfalls: [
      "Cabinet mono-ingénieur (gérant qui fait tout) → décote 30-40%",
      "Forte dépendance à 1-2 clients (> 40% du CA)",
      "Pas de spécialisation claire (généraliste sans niche) — moins défendable",
      "Qualifications expirées ou en cours de renouvellement difficile",
      "Logiciels métiers anciens (passage à BIM/Revit non engagé)",
    ],
    valueOptimizations: [
      "Obtenir/renouveler les qualifications OPQIBI dans la spécialisation phare",
      "Recruter et fidéliser des ingénieurs seniors avec carnet personnel",
      "Diversifier les donneurs d'ordre (objectif aucun > 20% du CA)",
      "Migrer vers BIM (Revit, IFC) si pas déjà fait — impact valeur fort",
      "Présenter 3 ans de comptes propres + ratios : taux d'utilisation, TJM ingénieur",
    ],
    sectorSpecifics: [
      "Convention collective Syntec (IDCC 1486) ou Bureaux d'études (selon statut)",
      "Garantie décennale obligatoire (assurance professionnelle)",
      "TVA 20% sur les honoraires",
      "Qualifications OPQIBI/OPQTECC : critères d'attribution des marchés publics",
      "Forte demande secteur (rénovation énergétique, BIM imposé sur projets publics > 25M€)",
    ],
    buyerProfile: "Ingénieur expérimenté en première installation (35%), BET en croissance externe (30%), grand groupe ingénierie (Egis, Setec, Artelia, 25%), reprise interne (10%).",
    fiscalTip: "Cession via holding souvent pertinente. Pacte Dutreil pour transmission familiale. Régime IS (SAS/SARL). Article 150-0 B ter pour report fiscal.",
  },
  {
    slug: 'transport-logistique',
    name: 'société de transport',
    nameLong: 'société de transport routier de marchandises (TRM)',
    article: 'une',
    emoji: '🚚',
    color: '#0A1929',
    searchVolume: '210/mois',
    multiplesCa: { min: 0.20, max: 0.55, typical: 0.35 },
    multiplesEbe: { min: 3, max: 5, typical: 4 },
    example: { ca: 1500000, marge: '32%', ebe: 110000, location: 'Reims', value_low: 350000, value_central: 525000, value_high: 800000 },
    intro: "La société de transport routier de marchandises (TRM) est un secteur capitalistique (flotte de véhicules + chauffeurs) dont la valorisation s'établit sur des multiples faibles du CA mais doit toujours intégrer la valeur de la flotte (camions, semi-remorques) à part. Les multiples EBE sont plus pertinents.",
    valueFactors: [
      "Flotte récente (camions < 5 ans, semi-remorques à jour Euro VI)",
      "Capacité (licence de transport) obtenue ou rachetable (Capacité de transport pour véhicules > 3,5T)",
      "Mix clients : industriels (récurrent), distribution (volume), e-commerce (croissant)",
      "Chauffeurs qualifiés en CDI (CACES, FIMO/FCO à jour)",
      "Contrats long terme avec donneurs d'ordre (>1 an)",
      "Outils de gestion modernes (TMS, géoloc, optimisation tournées)",
      "Implantation stratégique (axe autoroutier, dépôt fonctionnel)",
    ],
    valuePitfalls: [
      "Flotte vieillissante (camions > 8 ans) → coût renouvellement très lourd (80-150K€ unitaire)",
      "Forte dépendance à 1-2 donneurs d'ordre (> 40% du CA)",
      "Marges écrasées (carburant, péages, salaires) — risque non rentabilité",
      "Chauffeurs en intérim → coûts variables élevés, instabilité",
      "Aucune visibilité contractuelle (commandes spot uniquement)",
    ],
    valueOptimizations: [
      "Renouveler la flotte (LOA/crédit-bail) avant la vente — valorisation directe",
      "Sécuriser les contrats donneurs d'ordre via contrats-cadres multi-années",
      "Investir dans un TMS (Akanea, Transwide, Acteos) — optimisation tournées",
      "Réduire le taux de retour à vide (mutualisation, partenariats)",
      "Présenter ratios précis : coût/km, taux d'utilisation flotte, marge par client",
    ],
    sectorSpecifics: [
      "Convention collective Transports routiers de marchandises (IDCC 16)",
      "Licence de transport obligatoire (Capacité professionnelle TRM)",
      "Réglementation européenne (RGPD, mobility package, ZFE)",
      "TVA 20% (sauf transport international)",
      "Sensibilité forte au prix du gasoil (50%+ des coûts variables)",
    ],
    buyerProfile: "Transporteur en croissance externe (40%), groupe logistique (FM Logistic, GEFCO, Heppner, 30%), repreneur en première installation (15%), investisseur (15%). Financement : crédit-bail flotte + apport pour le fonds.",
    fiscalTip: "Cession flotte (LOA, crédit-bail) à dissocier du fonds. Article 151 septies si CA < 350K€ (transport). Régime BIC ou IS selon structure.",
  },
  {
    slug: 'ecommerce',
    name: 'e-commerce',
    nameLong: 'site e-commerce indépendant (D2C / niche)',
    article: 'un',
    emoji: '🛒',
    color: '#0A1929',
    searchVolume: '590/mois',
    multiplesCa: { min: 0.30, max: 1.20, typical: 0.65 },
    multiplesEbe: { min: 3, max: 6, typical: 4.5 },
    example: { ca: 600000, marge: '45%', ebe: 75000, location: 'Lille', value_low: 250000, value_central: 380000, value_high: 600000 },
    intro: "Le site e-commerce est un actif digital dont la valorisation dépend fortement de la nature du business (D2C marque propre vs revente vs dropshipping), de la stratégie d'acquisition (organique vs paid) et de la récurrence des achats. Les pure players bien construits avec marque propre se valorisent comme des SaaS modestes.",
    valueFactors: [
      "Marque propre (D2C) vs revente (la marque vaut beaucoup plus)",
      "Trafic organique (SEO) > 30% du trafic total — moins de dépendance Meta/Google Ads",
      "Taux de répétition d'achat > 30% (clients fidèles)",
      "AOV (panier moyen) > 50€ et marge brute > 40%",
      "Liste email opt-in > 10 000 contacts qualifiés",
      "Audience réseaux sociaux > 30K abonnés organiques",
      "Stock maîtrisé (rotation > 6x/an) et fournisseurs sécurisés",
    ],
    valuePitfalls: [
      "100% dépendance aux Ads Meta/Google (CAC en hausse, marge écrasée)",
      "Aucune récurrence (achat one-shot, pas de fidélisation)",
      "Dropshipping pur (pas de stock, pas de marque, valeur très faible)",
      "Plateforme dépendante (Shopify OK, marketplaces propriétaires non transférables)",
      "Stock obsolète ou périmé → moins-value à l'inventaire",
    ],
    valueOptimizations: [
      "Construire une marque (storytelling, packaging, communauté)",
      "Investir dans le SEO long terme (blog, backlinks, contenu)",
      "Mettre en place de l'abonnement / programme fidélité",
      "Diversifier les canaux (multi-marketplaces, B2B, retail)",
      "Présenter dashboards : CAC, LTV, AOV, taux répétition, mix canaux",
    ],
    sectorSpecifics: [
      "Pas de convention collective spécifique (Syntec ou commerce selon situation)",
      "RGPD strict (newsletter, cookies, données paiement)",
      "TVA à l'exportation (One-Stop-Shop OSS pour UE depuis 2021)",
      "Plateformes typiques : Shopify, WooCommerce, PrestaShop, Magento",
      "Marché concurrentiel mais en croissance (+8%/an France)",
    ],
    buyerProfile: "Entrepreneur digital cherchant un actif rentable (40%), e-commerçant en consolidation (30%), groupe (Webedia, Ankorstore, 20%), holding patrimoniale (10%). Plateformes type Empire Flippers, Flippa pour les ventes < 500K€.",
    fiscalTip: "Cession via holding (article 150-0 B ter) souvent optimisée. Pour les pure D2C : valoriser la propriété intellectuelle (marque déposée INPI). Régime IS.",
  },
];

// Helper
export function getBusinessBySlug(slug) {
  return BUSINESSES.find(b => b.slug === slug);
}
